// Application-wide configuration values and thresholds.
// @/src/config/constants.js
// Named constants for business rules, configurable thresholds, and external constraints.
// Time & math fundamentals (60, 1440, 1000, etc.) remain as literals — they are universal truths.

// ─── Business Logic & Domain Rules ───────────────────────────────────────────────

/** Snap increment for time blocks in the calendar grid (minutes). */
export const TIME_SLOT_MINS = 15;

/**
 * Default duration assigned to new tasks when none is specified (minutes).
 * This equals TIME_SLOT_MINS today, but they are distinct concepts:
 * TIME_SLOT_MINS is the calendar grid resolution;
 * DEFAULT_TASK_DURATION_MINS is the initial placeholder for untriaged tasks.
 */
export const DEFAULT_TASK_DURATION_MINS = 15;

/** Default height of a 15-minute slot in pixels for the planning grid. */
export const FIFTEEN_MINUTE_SLOT_HEIGHT = 20;

/** Number of days shown in the Horizon month/rolling view. */
export const HORIZON_VIEW_DAYS = 48;

/** Number of days to retain cached Google Calendar events. */
export const GCAL_RETENTION_DAYS = 7;

/** Maximum characters allowed in a task title. */
export const MAX_TASK_TITLE_LENGTH = 100;

/** Duration threshold (minutes) separating "small" from "medium" tasks in Triage view. */
export const SMALL_TASK_THRESHOLD_MINS = 30;

/** Duration threshold (minutes) separating "medium" from "large" tasks in Triage view. */
export const LONG_TASK_THRESHOLD_MINS = 120;

/** Calendar view start hour (24h format). */
export const CALENDAR_START_HOUR = 8;

/** Calendar view end hour (24h format). 23 = grid rows through 22:30 evening regime. */
export const CALENDAR_END_HOUR = 23;

/** Default number of days until a follow-up task is scheduled. */
export const DEFAULT_FOLLOWUP_DELAY_DAYS = 7;

// ─── UX Physics & Timings ────────────────────────────────────────────────────────

/** Duration a toast notification remains visible (milliseconds). */
export const TOAST_TIMEOUT_MS = 5000;

/** Minimum pixel drag distance to trigger a swipe action (complete/snooze). */
export const SWIPE_ACTION_THRESHOLD_PX = 80;

/** Debounce delay for search input (milliseconds). */
export const SEARCH_DEBOUNCE_DELAY_MS = 200;

/** Minimum interval between Google Calendar sync attempts (milliseconds). */
export const GCAL_SYNC_THROTTLE_MS = 900000; // 15 minutes (15 * 60 * 1000)

/** Long-press duration threshold (milliseconds). */
export const LONG_PRESS_DELAY_MS = 250;

/** Percentage of max workload at which a warning state is shown (0–1). */
export const WORKLOAD_WARNING_THRESHOLD = 0.8;

/** Minimum historical confidence to auto-suggest a context for a project (0–1). */
export const CONTEXT_SUGGESTION_CONFIDENCE = 0.8;

/** Percentage of planned task time elapsed before the focus timer shows a warning color (0–1). */
export const TIMER_WARNING_THRESHOLD = 0.8;

// ─── Time & Math Fundamentals ────────────────────────────────────────────────────

/** Milliseconds in a minute. Use for conversions: `elapsedMs / MS_PER_MINUTE`. */
export const MS_PER_MINUTE = 60000;

/** Small task bucket duration for triage view (minutes). */
export const SMALL_TASK_BUCKET_MINS = 10;

/** Minimum height in pixels for a GCal event block to ensure readability. */
export const MIN_GCAL_EVENT_HEIGHT_PX = 25;

/** Total minutes in a single day (24 * 60). */
export const MINUTES_PER_DAY = 1440;
